import { useState, useEffect } from "react";

const STORAGE_KEY = "cookie_consent";
const SIX_MONTHS_MS = 180 * 24 * 60 * 60 * 1000;

/**
 * CNIL-compliant cookie consent hook.
 * - consent === null → user hasn't chosen yet (banner should be shown)
 * - consent.analytics / consent.advertising → per-category booleans
 * - Consent stored 6 months max (CNIL recommendation), then banner reappears.
 * - Listen for "reopen-cookie-banner" event (dispatched from footer link).
 */
export const useConsent = () => {
  const [consent, setConsent] = useState(null);
  const [isBannerOpen, setIsBannerOpen] = useState(false);

  useEffect(() => {
    const loadConsent = () => {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored);
          const age = Date.now() - new Date(parsed.timestamp).getTime();
          if (age < SIX_MONTHS_MS) {
            setConsent(parsed);
            setIsBannerOpen(false);
            return;
          }
          localStorage.removeItem(STORAGE_KEY);
        }
        setIsBannerOpen(true);
      } catch (e) {
        setIsBannerOpen(true);
      }
    };

    loadConsent();

    const reopenListener = () => {
      setConsent(null);
      setIsBannerOpen(true);
    };
    window.addEventListener("reopen-cookie-banner", reopenListener);
    return () => window.removeEventListener("reopen-cookie-banner", reopenListener);
  }, []);

  const updateConsent = (newConsent) => {
    const withTimestamp = {
      ...newConsent,
      necessary: true,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(withTimestamp));
    setConsent(withTimestamp);
    setIsBannerOpen(false);
    // Notify listeners (ConsentScripts uses this to load GA/Meta dynamically)
    window.dispatchEvent(new CustomEvent("consent-updated", { detail: withTimestamp }));
  };

  return { consent, isBannerOpen, updateConsent };
};
